// src/pages/Dashboard/ArtistDashboardPage.jsx
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchMyClubs } from "../../services/clubsApi";

import {
  fetchMyArtistProfile,
  fetchMyArtworks,
  deleteMyArtwork,
} from "../../services/arthouseApi";

import CreatorTopBar from "../../components/ArtHouse/CreatorTopBar";
import { useCreateClubModal } from "../../components/Clubs/createClub/CreateClubModalContext";

function getClubId(c) {
  return c?.club_id ?? c?.id ?? c?.clubId ?? null;
}

function getClubRole(c) {
  return c?.role ?? c?.member_role ?? c?.membership_role ?? "member";
}

/* ----------------------------
   UI: Stat Pills (unchanged)
----------------------------- */
function StatPill({ label, value, hint }) {
  return (
    <div
      className="
        group relative overflow-hidden
        rounded-[26px] border border-white/10
        bg-white/[0.055] backdrop-blur-xl
        shadow-[0_18px_90px_rgba(0,0,0,0.45)]
        px-5 py-4
        transition
        hover:bg-white/[0.075]
        hover:shadow-[0_26px_120px_rgba(0,0,0,0.6)]
      "
    >
      <div className="pointer-events-none absolute -top-20 left-1/2 h-40 w-[640px] -translate-x-1/2 rounded-full bg-white/10 blur-3xl opacity-0 group-hover:opacity-100 transition" />
      <div className="pointer-events-none absolute -right-14 -top-14 h-40 w-40 rounded-full bg-sky-300/10 blur-2xl opacity-60" />

      <div className="flex items-center justify-between gap-3">
        <div className="text-[11px] tracking-[0.34em] uppercase text-white/55">
          {label}
        </div>

        <div className="h-7 w-7 rounded-full border border-white/10 bg-white/[0.04] backdrop-blur-xl flex items-center justify-center text-[11px] text-white/55">
          
        </div>
      </div>

      <div className="mt-3 text-[28px] leading-none font-extrabold text-white/90 tabular-nums">
        {value}
      </div>

      {hint ? (
        <div className="mt-2 text-xs text-white/50 leading-relaxed">{hint}</div>
      ) : null}

      <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
    </div>
  );
}

function LoadingSkeletonCard() {
  return (
    <div className="rounded-[24px] overflow-hidden border border-white/10 bg-white/[0.045] backdrop-blur-xl shadow-[0_18px_80px_rgba(0,0,0,0.45)]">
      <div className="relative aspect-[16/11] bg-black/25">
        <div className="absolute inset-0 animate-pulse">
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-black/30" />
          <div className="absolute left-4 top-4 h-3 w-24 rounded-full bg-white/10" />
          <div className="absolute left-4 top-9 h-4 w-40 rounded-full bg-white/10" />
          <div className="absolute right-4 top-4 h-3 w-16 rounded-full bg-white/10" />
          <div className="absolute left-4 right-4 bottom-4 h-10 rounded-2xl bg-white/10" />
        </div>
      </div>
    </div>
  );
}

function EmptyState({ title, subtitle, onReset, showReset }) {
  return (
    <div className="mt-6 rounded-[28px] border border-white/10 bg-white/[0.05] backdrop-blur-xl shadow-[0_26px_120px_rgba(0,0,0,0.55)] overflow-hidden">
      <div className="p-8 sm:p-10">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-2xl border border-white/10 bg-white/[0.05] flex items-center justify-center text-white/80">
            ⬡
          </div>

          <div className="min-w-0">
            <div className="text-[12px] tracking-[0.34em] uppercase text-white/55">
              Nothing here
            </div>
            <h3 className="mt-2 text-xl sm:text-2xl font-extrabold text-white/90">
              {title}
            </h3>
            <p className="mt-2 text-sm text-white/60 max-w-[70ch] leading-relaxed">
              {subtitle}
            </p>

            {showReset ? (
              <button
                type="button"
                onClick={onReset}
                className="
                  mt-5 inline-flex items-center gap-2
                  rounded-full border border-white/10
                  bg-white/[0.05] hover:bg-white/[0.08]
                  px-4 py-2.5
                  text-sm font-semibold text-white/80
                  transition
                "
              >
                Reset filters <span className="opacity-70">↺</span>
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <div className="h-px bg-gradient-to-r from-transparent via-white/12 to-transparent" />
      <div className="p-6 text-xs text-white/45">
        Tip: titles + tags are searchable.
      </div>
    </div>
  );
}

/* ----------------------------
   Clubs UI (simplified: sleek badges)
----------------------------- */
function ClubsPanelMinimal({ loading, error, owned, joined, onOpenClub }) {
  const Row = ({ club }) => {
    const name = club?.name || "Club";
    const slug = club?.slug || "";
    const subtitle =
      club?.description?.trim() ||
      (club?.visibility ? String(club.visibility).toLowerCase() : "");

    return (
      <button
        type="button"
        onClick={() => onOpenClub?.(club)}
        className="
          group w-full text-left
          rounded-2xl
          border border-white/10
          bg-white/[0.03]
          hover:bg-white/[0.05]
          transition
          px-4 py-3
          shadow-[0_14px_60px_rgba(0,0,0,0.28)]
          focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/25
        "
        title={slug ? `Open /clubs/${slug}` : "Open club"}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-extrabold text-white/92 truncate">
              {name}
            </div>
            {subtitle ? (
              <div className="mt-1 text-xs text-white/62 truncate">
                {subtitle}
              </div>
            ) : null}
          </div>

          <div className="shrink-0 text-white/35 group-hover:text-white/60 transition">
            →
          </div>
        </div>
      </button>
    );
  };

  const Skeleton = () => (
    <div className="space-y-2">
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          key={i}
          className="h-12 rounded-2xl border border-white/10 bg-white/[0.03] animate-pulse"
        />
      ))}
    </div>
  );

  const Empty = ({ text }) => <div className="text-sm text-white/55">{text}</div>;

  const stableKey = (c, fallbackPrefix) =>
    String(
      getClubId(c) ??
        c?.slug ??
        `${fallbackPrefix}-${c?.name ?? "club"}-${c?.visibility ?? ""}`
    );

  return (
    <div className="mt-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="mt-2 text-md sm:text-xl tracking-[0.42em] uppercase text-white/85 ">
            Clubs
          </div>
        </div>

        {error ? (
          <div className="mt-1 text-xs text-red-200/90 border border-red-400/25 bg-red-400/12 px-3 py-2 rounded-full">
            {error}
          </div>
        ) : null}
      </div>

      <div className="mt-6 grid grid-cols-1 xl:grid-cols-2 gap-10">
        <div className="min-w-0">
          <div className="flex items-center justify-between">
            <div className="text-[10px] tracking-[0.34em] uppercase text-white/65">
              Owned
            </div>
            <div className="text-xs text-white/45 tabular-nums">
              {owned.length}
            </div>
          </div>

          <div className="mt-3 space-y-2">
            {loading ? (
              <Skeleton />
            ) : owned.length ? (
              owned.map((c) => <Row key={stableKey(c, "owned")} club={c} />)
            ) : (
              <Empty text="No owned clubs yet." />
            )}
          </div>
        </div>

        <div className="min-w-0">
          <div className="flex items-center justify-between">
            <div className="text-[10px] tracking-[0.34em] uppercase text-white/65">
              Joined
            </div>
            <div className="text-xs text-white/45 tabular-nums">
              {joined.length}
            </div>
          </div>

          <div className="mt-3 space-y-2">
            {loading ? (
              <Skeleton />
            ) : joined.length ? (
              joined.map((c) => <Row key={stableKey(c, "joined")} club={c} />)
            ) : (
              <Empty text="No joined clubs yet." />
            )}
          </div>
        </div>
      </div>

      <div className="mt-7 h-px bg-gradient-to-r from-transparent via-white/12 to-transparent" />
    </div>
  );
}

/* ----------------------------
   Artwork card (unchanged)
----------------------------- */
function ArtworkRowCard({ a, isMenuOpen, onToggleMenu, onView, onEdit, onDelete }) {
  const tags = Array.isArray(a?.tags) ? a.tags : [];

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onView(a)}
      onKeyDown={(e) => {
        if (e.key === "Enter") onView(a);
      }}
      className="
        group relative rounded-[24px] overflow-hidden cursor-pointer
        border border-white/10
        bg-white/[0.045] backdrop-blur-xl
        shadow-[0_18px_80px_rgba(0,0,0,0.45)]
        transition
        hover:bg-white/[0.075]
        hover:shadow-[0_26px_120px_rgba(0,0,0,0.65)]
        hover:-translate-y-[2px]
        focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300/40
      "
    >
      <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition">
        <div className="absolute -inset-[1px] rounded-[26px] bg-gradient-to-br from-sky-300/20 via-transparent to-cyan-300/10" />
      </div>

      <div className="relative aspect-[16/11] bg-black/25">
        {a?.image_url && (
          <img
            src={a.image_url}
            alt={a.title || "Artwork"}
            className="
              absolute inset-0 h-full w-full object-cover
              opacity-[0.94]
              transition
              group-hover:scale-[1.02]
              group-hover:opacity-[0.98]
              will-change-transform
            "
            loading="lazy"
          />
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/18 to-black/28" />
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_30%_10%,rgba(255,255,255,0.10),transparent_55%)] opacity-60" />

        <div className="absolute left-4 right-4 top-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <div className="text-[10px] tracking-[0.34em] uppercase text-white/55">
                Artwork
              </div>
              {tags.length ? (
                <div className="hidden sm:flex items-center gap-1">
                  {tags.slice(0, 2).map((t) => (
                    <span
                      key={t}
                      className="
                        rounded-full border border-white/12
                        bg-black/25 backdrop-blur-xl
                        px-2 py-0.5
                        text-[10px] font-semibold text-white/70
                      "
                    >
                      {t}
                    </span>
                  ))}
                  {tags.length > 2 ? (
                    <span className="text-[10px] text-white/50">
                      +{tags.length - 2}
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="mt-1 font-semibold text-white/90 truncate">
              {a.title || "Untitled"}
            </div>
          </div>

          <div className="text-xs text-white/55 whitespace-nowrap">
            {a.created_at ? new Date(a.created_at).toLocaleDateString() : ""}
          </div>
        </div>

        <div className="absolute top-3 right-3">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleMenu(a.id);
            }}
            className="
              h-10 w-10 rounded-full
              border border-white/12
              bg-black/30 backdrop-blur-xl
              hover:bg-black/45
              transition
              flex items-center justify-center
              text-white/85
              shadow-[0_10px_30px_rgba(0,0,0,0.35)]
              focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300/40
            "
            title="Manage"
            aria-expanded={isMenuOpen ? "true" : "false"}
          >
            <span className="text-lg leading-none">⋯</span>
          </button>

          <div
            className={[
              "absolute right-0 mt-2 w-[180px]",
              "rounded-2xl border border-white/12",
              "bg-[#0A1022]/90 backdrop-blur-xl",
              "shadow-[0_24px_90px_rgba(0,0,0,0.65)]",
              "overflow-hidden z-30",
              "origin-top-right transition duration-150",
              isMenuOpen
                ? "opacity-100 scale-100 translate-y-0 pointer-events-auto"
                : "opacity-0 scale-[0.98] -translate-y-1 pointer-events-none",
            ].join(" ")}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => onEdit(a)}
              className="
                w-full px-4 py-3 text-left
                text-sm font-semibold text-white/85
                hover:bg-white/[0.06]
                transition
                flex items-center gap-2
              "
            >
              Edit
            </button>

            <div className="h-px bg-white/10" />

            <button
              type="button"
              onClick={() => onDelete(a)}
              className="
                w-full px-4 py-3 text-left
                text-sm font-semibold text-red-200
                hover:bg-red-400/10
                transition
                flex items-center gap-2
              "
            >
              Delete
            </button>
          </div>
        </div>

        <div className="absolute left-4 right-4 bottom-4 rounded-2xl border border-white/10 bg-black/25 backdrop-blur-xl p-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 text-xs text-white/70">
              <span className="tabular-nums">{a.likes ?? 0}</span>
              <span className="tabular-nums">{a.views ?? 0}</span>
              <span className="tabular-nums">{a.comments ?? 0}</span>
            </div>

            <div className="opacity-0 group-hover:opacity-100 transition text-xs text-white/70">
              View{" "}
              <span className="inline-block translate-x-0 group-hover:translate-x-0.5 transition">
                →
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/12 to-transparent" />
    </div>
  );
}

function getArtworkSnapshot(a) {
  if (!a) return null;

  const image_url =
    a?.image_url ||
    a?.imageUrl ||
    a?.thumbnail_url ||
    a?.thumb_url ||
    a?.cover_url ||
    a?.media_url ||
    "";

  return {
    id: a?.id ?? a?.artwork_id ?? a?.artworkId ?? null,
    title: a?.title || "Untitled",
    description: a?.description || a?.caption || a?.subtitle || "",
    image_url,
    tags: Array.isArray(a?.tags) ? a.tags : [],
    merchLinks: Array.isArray(a?.merchLinks) ? a.merchLinks : [],
    views: a?.views,
    likes: a?.likes,
    artist: a?.artist || a?.creator || null,
  };
}

export default function ArtistDashboardPage() {
  const nav = useNavigate();
  const location = useLocation();
  const { openCreateClubModal } = useCreateClubModal();

  const [loading, setLoading] = useState(true);
  const [artist, setArtist] = useState(null);
  const [artworks, setArtworks] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [flash, setFlash] = useState("");

  const [q, setQ] = useState("");
  const [sort, setSort] = useState("new");

  const [openMenuId, setOpenMenuId] = useState(null);

  // Clubs state
  const [clubsLoading, setClubsLoading] = useState(true);
  const [clubsError, setClubsError] = useState("");
  const [myClubs, setMyClubs] = useState([]);

  useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);
      setErrorMsg("");
      setClubsLoading(true);
      setClubsError("");

      try {
        const profile = await fetchMyArtistProfile();
        const myArt = await fetchMyArtworks();

        // clubs best-effort (won’t block dashboard)
        try {
          const clubsPayload = await fetchMyClubs();
          if (alive) {
            setMyClubs(
              Array.isArray(clubsPayload?.clubs) ? clubsPayload.clubs : []
            );
            setClubsError("");
          }
        } catch (e) {
          if (alive) setClubsError(e?.message || "Failed to load clubs.");
        } finally {
          if (alive) setClubsLoading(false);
        }

        if (!alive) return;

        setArtist(profile?.artist || null);
        setArtworks(Array.isArray(myArt?.artworks) ? myArt.artworks : []);
      } catch (e) {
        if (!alive) return;
        setErrorMsg(e?.message || "Failed to load dashboard.");
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const close = () => setOpenMenuId(null);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setOpenMenuId(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const stats = useMemo(() => {
    const total = artworks.length;
    const likes = artworks.reduce((acc, a) => acc + (a.likes ?? 0), 0);
    const views = artworks.reduce((acc, a) => acc + (a.views ?? 0), 0);
    const comments = artworks.reduce((acc, a) => acc + (a.comments ?? 0), 0);
    return { total, likes, views, comments };
  }, [artworks]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let list = [...artworks];

    if (needle) {
      list = list.filter((a) =>
        `${a.title || ""} ${(a.tags || []).join(" ")}`.toLowerCase().includes(needle)
      );
    }

    list.sort((a, b) => {
      if (sort === "likes") return (b.likes ?? 0) - (a.likes ?? 0);
      if (sort === "views") return (b.views ?? 0) - (a.views ?? 0);
      if (sort === "old") return new Date(a.created_at) - new Date(b.created_at);
      return new Date(b.created_at) - new Date(a.created_at);
    });

    return list;
  }, [artworks, q, sort]);

  const handleReset = () => {
    setQ("");
    setSort("new");
  };

  function handleView(a) {
    const artistId = artist?.id ?? artist?.artist_id ?? null;

    if (!artistId) {
      nav(`/arthouse/art/${a.id}`);
      return;
    }

    nav(`/arthouse/artist-modal/${artistId}`, {
      state: {
        backgroundLocation: location,
        artwork: getArtworkSnapshot(a),
      },
    });
  }

  function handleEdit(a) {
    setOpenMenuId(null);
    nav(`/dashboard/art/${a.id}/edit`);
  }

  async function handleDelete(a) {
    setOpenMenuId(null);

    const ok = window.confirm(
      `Delete "${a.title || "Untitled"}" permanently? This cannot be undone.`
    );
    if (!ok) return;

    setErrorMsg("");
    setFlash("");

    try {
      await deleteMyArtwork(a.id);
      setArtworks((prev) => prev.filter((x) => String(x.id) !== String(a.id)));

      setFlash("Deleted ");
      setTimeout(() => setFlash(""), 1400);
    } catch (e) {
      setErrorMsg(e?.message || "Failed to delete artwork.");
    }
  }

  function toggleMenu(id) {
    setOpenMenuId((prev) => (String(prev) === String(id) ? null : id));
  }

  const resultLabel = useMemo(() => {
    const total = artworks.length;
    const shown = filtered.length;
    if (!q.trim()) return `${shown} of ${total}`;
    return `${shown} result${shown === 1 ? "" : "s"} · filtered`;
  }, [artworks.length, filtered.length, q]);

  const { ownedClubs, joinedClubs } = useMemo(() => {
    const owned = [];
    const joined = [];
    for (const c of myClubs) {
      const role = String(getClubRole(c) || "").toLowerCase();
      if (role === "owner") owned.push(c);
      else joined.push(c);
    }
    return { ownedClubs: owned, joinedClubs: joined };
  }, [myClubs]);

  const openClub = (club) => {
    if (!club) return;
    const slug = club?.slug;
    const id = getClubId(club);
    if (slug) nav(`/clubs/${encodeURIComponent(slug)}`);
    else if (id != null) nav(`/clubs/${encodeURIComponent(String(id))}`);
  };

  return (
    <div className="min-h-screen bg-[#070B16] text-white">

      {/* ambience */}
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute -top-44 left-16 h-[560px] w-[560px] rounded-full bg-cyan-400/10 blur-[150px]" />
        <div className="absolute top-28 -right-52 h-[720px] w-[720px] rounded-full bg-sky-400/10 blur-[170px]" />
        <div className="absolute -bottom-60 left-1/2 h-[720px] w-[720px] -translate-x-1/2 rounded-full bg-indigo-400/5 blur-[170px]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_10%,rgba(56,189,248,0.08),transparent_45%),radial-gradient(circle_at_80%_15%,rgba(125,211,252,0.06),transparent_50%),radial-gradient(circle_at_55%_70%,rgba(99,102,241,0.05),transparent_55%)]" />
        <div className="absolute inset-0 opacity-[0.18] [background-image:linear-gradient(to_right,rgba(255,255,255,0.06)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:44px_44px]" />
      </div>

      <div className="relative mx-auto max-w-[1400px] px-4 pt-8 pb-14">
        <CreatorTopBar />

        {flash ? (
          <div className="mt-4 rounded-2xl border border-sky-300/25 bg-sky-300/10 px-4 py-3 text-sm text-sky-200 shadow-[0_18px_80px_rgba(0,0,0,0.35)]">
            {flash}
          </div>
        ) : null}

        {/* Header / Search */}
        <div className="mt-6 rounded-[30px] border border-white/10 bg-white/[0.055] backdrop-blur-xl shadow-[0_26px_120px_rgba(0,0,0,0.55)] overflow-hidden">
          <div className="px-6 py-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-[240px]">
                <div className="text-[11px] tracking-[0.38em] uppercase text-white/55">
                  Creator Dashboard
                </div>

                <div className="mt-2 flex items-end gap-3 flex-wrap">
                  <h1 className="text-3xl font-extrabold tracking-tight text-white/90">
                    @{artist?.display_name || "artist"}
                  </h1>
                  <div className="pb-1 text-xs text-white/50">
                    <span className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1">
                      {resultLabel}
                    </span>
                  </div>
                </div>

                <p className="mt-2 text-sm text-white/60 max-w-[72ch] leading-relaxed">
                  Your studio overview — stats, search, and performance across your uploads.
                </p>
              </div>

              <div className="w-full max-w-[760px]">
                <div className="rounded-[26px] border border-white/10 bg-black/20 px-4 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]">
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    {/* Search */}
                    <div className="flex-1 min-w-[240px]">
                      <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2.5 focus-within:border-sky-300/25 focus-within:bg-white/[0.045] transition">
                        <span className="opacity-60 text-sm select-none">⌕</span>
                        <input
                          value={q}
                          onChange={(e) => setQ(e.target.value)}
                          placeholder="Search your uploads…"
                          className="w-full bg-transparent outline-none text-[14px] text-white/90 placeholder:text-white/40"
                        />
                        {q ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setQ("");
                            }}
                            className="h-8 w-8 rounded-full border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition text-white/70 flex items-center justify-center"
                            title="Clear"
                          >
                            ×
                          </button>
                        ) : null}
                      </div>
                    </div>

                    {/* Create Club */}
                    <button
                      type="button"
                      onClick={() => openCreateClubModal()}
                      className="
                        h-11 px-5 rounded-full
                        border border-emerald-300/25
                        bg-emerald-300/10
                        hover:bg-emerald-300/15
                        transition
                        text-sm font-extrabold tracking-wide
                        text-emerald-200
                        shadow-[0_18px_80px_rgba(0,0,0,0.35)]
                        focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/35
                      "
                    >
                      Create Club
                    </button>

                    {/* Sort */}
                    <div className="relative">
                      <select
                        value={sort}
                        onChange={(e) => setSort(e.target.value)}
                        className="
                          h-11 pl-4 pr-10 rounded-full
                          border border-white/10
                          bg-white/[0.04]
                          text-sm font-semibold text-white/80
                          outline-none
                          hover:bg-white/[0.07]
                          transition
                          appearance-none
                        "
                      >
                        <option value="new" className="bg-[#070B16] text-white">
                          Newest
                        </option>
                        <option value="old" className="bg-[#070B16] text-white">
                          Oldest
                        </option>
                        <option value="likes" className="bg-[#070B16] text-white">
                          Most likes
                        </option>
                        <option value="views" className="bg-[#070B16] text-white">
                          Most views
                        </option>
                      </select>

                      <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-white/50">
                        ▾
                      </div>
                    </div>

                    {/* Reset */}
                    <button
                      type="button"
                      onClick={handleReset}
                      className="
                        h-11 px-4 rounded-full
                        border border-white/10
                        bg-white/[0.04]
                        hover:bg-white/[0.07]
                        transition
                        text-sm font-semibold text-white/75
                      "
                    >
                      Reset
                    </button>
                  </div>
                </div>

                <div className="mt-2 text-xs text-white/45">
                  Pro tip: Press{" "}
                  <span className="text-white/70 font-semibold">Esc</span> to close menus.
                </div>
              </div>
            </div>

            {errorMsg ? (
              <div className="mt-4 rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm shadow-[0_18px_80px_rgba(0,0,0,0.35)]">
                {errorMsg}
              </div>
            ) : null}
          </div>
        </div>

        {/* Stats */}
        {loading ? (
          <>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatPill label="Uploads" value={"—"} hint="Total pieces" />
              <StatPill label="Likes" value={"—"} hint="Across all posts" />
              <StatPill label="Views" value={"—"} hint="Total reach" />
              <StatPill label="Comments" value={"—"} hint="Engagement" />
            </div>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {Array.from({ length: 6 }).map((_, i) => (
                <LoadingSkeletonCard key={i} />
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatPill label="Uploads" value={stats.total} hint="Total pieces" />
              <StatPill label="Likes" value={stats.likes} hint="Across all posts" />
              <StatPill label="Views" value={stats.views} hint="Total reach" />
              <StatPill label="Comments" value={stats.comments} hint="Engagement" />
            </div>

            {/* Clubs */}
            <ClubsPanelMinimal
              loading={clubsLoading}
              error={clubsError}
              owned={ownedClubs}
              joined={joinedClubs}
              onOpenClub={openClub}
            />

            {/* Artworks grid */}
            {artworks.length === 0 ? (
              <EmptyState
                title="No uploads yet"
                subtitle="Once you post your first piece, it’ll show up here with likes, views, and comments — plus edit/delete controls via the menu button."
                onReset={() => {}}
                showReset={false}
              />
            ) : filtered.length === 0 ? (
              <EmptyState
                title="No matches for your search"
                subtitle="Try a shorter keyword, or search by tags. You can also reset filters to return to your full gallery."
                onReset={handleReset}
                showReset={true}
              />
            ) : (
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {filtered.map((a) => (
                  <ArtworkRowCard
                    key={a.id}
                    a={a}
                    isMenuOpen={String(openMenuId) === String(a.id)}
                    onToggleMenu={toggleMenu}
                    onView={handleView}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
